/**
 * Pull an existing playEngine version back into the snapshot pool.
 *
 * The mirror of "Export as version", and it narrows the same way: which company
 * the version lives in, which base, which version, then where it lands and what
 * it is called. The last two are optional — the root, and a name derived from
 * the version's title. A name clash is suffixed rather than refused.
 *
 * The company comes first for the same reason it does in the export dialog:
 * versions are held per company, so the same base carries a different set of
 * them for each one, and until the company is known the base list means nothing.
 * It is only offered when the user may move playables between more than one —
 * everyone else has a single company and never sees the field.
 *
 * The version list is the one that runs long, so its menu opens with a search
 * box at the top that filters it by title.
 *
 * The version's assets are stored as files plus a mongo record and referenced as
 * "@server:<id>"; a snapshot carries the bytes inline. The server does that
 * conversion, so what arrives in the pool opens like any other snapshot.
 */

import React, { useEffect, useMemo, useState } from "react";
import _ from "lodash";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  ListSubheader,
  MenuItem,
  TextField,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import SearchIcon from "@material-ui/icons/Search";
import {
  importVersionAsSnapshot,
  listSourceVersions,
  listVersionSources,
} from "../../modules/snapshotEditorApi";
import { collectFolderPaths } from "../../modules/snapshotTree";
import { FIELD_LABEL } from "./panelPalette";

const useStyles = makeStyles({
  field: { marginTop: "12px" },
  hint: { fontSize: "12px", color: FIELD_LABEL, opacity: 0.8, marginTop: "10px", lineHeight: 1.6 },
  failure: { marginTop: "10px", fontSize: "12px", color: "#ef9a9a" },
  busy: { display: "flex", alignItems: "center", gap: "10px", marginTop: "12px" },
  // ListSubheader is sticky by default, which is what keeps the box in view
  // however far down the list is scrolled; its 48px line height is not.
  menuSearch: { padding: "8px 16px 4px", lineHeight: "normal", zIndex: 2 },
  menuSearchCount: { fontSize: "11px", color: FIELD_LABEL, whiteSpace: "nowrap" },
  menuSearchEmpty: { fontSize: "12px", color: FIELD_LABEL, padding: "8px 0 4px" },
});

/**
 * The search box at the top of the Version menu.
 *
 * It lives INSIDE a Select's menu, which is hostile ground for an input:
 *   - the Select clones every child as an option, with an onClick that selects
 *     the child's value and closes the menu — so the props it injects are
 *     received here and dropped;
 *   - the menu list moves the focus to the option starting with any key pressed
 *     inside it — so keys stop at the box, except Escape and Tab, which still
 *     close the menu the way they do on every other select;
 *   - the Menu hands its first child a ref to position itself against — so one
 *     is forwarded to a real element.
 * ArrowDown hands the focus to the first listed version; from there the menu's
 * own keyboard handling takes over.
 */
const VersionSearchRow = React.forwardRef(function VersionSearchRow(
  { query, onQueryChange, matchCount, total },
  ref
) {
  const classes = useStyles();
  const trimmed = query.trim();

  return (
    <ListSubheader ref={ref} className={classes.menuSearch}>
      <TextField
        value={query}
        onChange={(event) => onQueryChange(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "ArrowDown") {
            event.preventDefault();
            const firstOption = event.currentTarget
              .closest("[role='listbox']")
              ?.querySelector("[role='option']:not([style*='display: none'])");
            if (firstOption) firstOption.focus();
          }
          if (event.key !== "Escape" && event.key !== "Tab") event.stopPropagation();
        }}
        placeholder="Search versions"
        autoFocus
        fullWidth
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
          endAdornment: trimmed ? (
            <InputAdornment position="end">
              <span className={classes.menuSearchCount}>
                {matchCount} / {total}
              </span>
            </InputAdornment>
          ) : null,
        }}
      />
      {trimmed && matchCount === 0 && (
        <Typography className={classes.menuSearchEmpty}>No version matches &ldquo;{trimmed}&rdquo;.</Typography>
      )}
    </ListSubheader>
  );
});

/**
 * The companies a version may be pulled out of: the one being looked at, plus
 * the user's export targets.
 *
 * The session endpoint reports the targets without the user's own company in
 * them (a superAdmin is the exception — theirs is every company), so it is put
 * back here. Leaving it out would make the field offer everywhere except the
 * place the user is standing.
 */
function readSourceCompanies() {
  const activeID = localStorage.getItem("user.companyID");
  const active = activeID
    ? [{ _id: String(activeID), name: localStorage.getItem("user.companyName") || "This company" }]
    : [];

  let targets = [];
  try {
    targets = JSON.parse(localStorage.getItem("user.exportTargets") || "[]");
  } catch (e) {
    targets = [];
  }

  return _.uniqBy([...active, ...targets], (company) => String(company._id));
}

export default function DialogImportVersion({
  editor,
  open,
  onClose,
  createSnackbar,
  draftFolders,
  onImported,
}) {
  const classes = useStyles();

  const sourceCompanies = useMemo(readSourceCompanies, [open]);
  const [sourceCompanyID, setSourceCompanyID] = useState(
    () => localStorage.getItem("user.companyID") || ""
  );
  const [sourceBases, setSourceBases] = useState([]);
  const [sourceBaseID, setSourceBaseID] = useState("");
  const [versions, setVersions] = useState([]);
  const [sourceVersionID, setSourceVersionID] = useState("");
  const [fileName, setFileName] = useState("");
  const [folderPath, setFolderPath] = useState("");
  // What is typed into the search at the top of the Version menu. Emptied each
  // time the menu opens, so every opening starts from the whole list.
  const [versionQuery, setVersionQuery] = useState("");
  const [areBasesLoading, setAreBasesLoading] = useState(false);
  const [areVersionsLoading, setAreVersionsLoading] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [failure, setFailure] = useState(null);

  // The dialog is mounted permanently by the snapshot list, so each open has to
  // clear what the previous run left behind.
  useEffect(() => {
    if (!open) return;
    setSourceCompanyID(localStorage.getItem("user.companyID") || "");
    setSourceBaseID("");
    setSourceVersionID("");
    setVersions([]);
    setFileName("");
    setFolderPath("");
    setFailure(null);
  }, [open]);

  // Which bases that company holds. Re-asked whenever the company changes: the
  // grant is per company, so the two lists have no reason to overlap.
  useEffect(() => {
    if (!open || !editor.baseID) return undefined;

    let isCurrent = true;
    setAreBasesLoading(true);
    setSourceBaseID("");
    setSourceBases([]);
    listVersionSources(editor.baseID, { sourceCompanyID })
      .then((response) => {
        if (!isCurrent) return;
        if (!response.ok) {
          setFailure(_.get(response, "error.error") || response.error);
          return;
        }
        setSourceBases(_.get(response, "data.targets", []));
      })
      .finally(() => {
        if (isCurrent) setAreBasesLoading(false);
      });

    return () => {
      isCurrent = false;
    };
  }, [open, editor.baseID, sourceCompanyID]);

  useEffect(() => {
    if (!sourceBaseID) {
      setVersions([]);
      return undefined;
    }

    let isCurrent = true;
    setAreVersionsLoading(true);
    setSourceVersionID("");
    listSourceVersions(editor.baseID, { sourceBaseID, sourceCompanyID })
      .then((response) => {
        if (!isCurrent) return;
        if (!response.ok) {
          setFailure(_.get(response, "error.error") || response.error);
          setVersions([]);
          return;
        }
        const records = _.get(response, "data.versions", []);
        setVersions(
          [...records].sort((a, b) => new Date(b.dateUpdated || 0) - new Date(a.dateUpdated || 0))
        );
      })
      .finally(() => {
        if (isCurrent) setAreVersionsLoading(false);
      });

    return () => {
      isCurrent = false;
    };
  }, [editor.baseID, sourceBaseID, sourceCompanyID]);

  // Grouped by game: base titles repeat across games ("Base 1", "Main") and the
  // game name is the half that tells them apart.
  const baseOptions = useMemo(() => {
    const rows = [];
    let lastGameID = null;
    for (const base of sourceBases) {
      if (base.gameID !== lastGameID) {
        rows.push({ isHeader: true, key: `game-${base.gameID}`, label: base.gameTitle });
        lastGameID = base.gameID;
      }
      rows.push({ isHeader: false, key: base.baseID, label: base.title, value: base.baseID });
    }
    return rows;
  }, [sourceBases]);

  // The version search. Matched on the title, ignoring case. The chosen version
  // stays a child — hidden — when it does not match: the Select reads what to
  // show in the closed field from its children, and would go blank without it.
  const { listedVersions, versionMatchCount } = useMemo(() => {
    const needle = versionQuery.trim().toLowerCase();
    const listed = [];
    let count = 0;
    for (const version of versions) {
      const isMatch = !needle || (version.title || "").toLowerCase().includes(needle);
      if (isMatch) count += 1;
      if (isMatch || version.versionID === sourceVersionID) {
        listed.push({ ...version, isHidden: !isMatch });
      }
    }
    return { listedVersions: listed, versionMatchCount: count };
  }, [versions, versionQuery, sourceVersionID]);

  // A snapshot's folder is metadata on the file, not a directory, so a folder
  // only exists on disk once a snapshot is in it. One just created in the tree
  // and not yet filled lives only there, as a draft — handed in so it can be
  // picked, since filling it is exactly what a draft is waiting for. Put through
  // the same helper as the snapshots' own folders, so a draft "A/B" offers "A".
  const folders = useMemo(
    () =>
      collectFolderPaths([
        ...(editor.snapshots || []),
        ...(draftFolders || []).map((folderPath) => ({ exportFolder: folderPath })),
      ]),
    [editor.snapshots, draftFolders]
  );

  const canImport = Boolean(sourceVersionID) && !isImporting && !areVersionsLoading;

  async function runImport() {
    if (!canImport) return;

    setIsImporting(true);
    setFailure(null);
    // The field takes a stem; the API only accepts `.json` names.
    const stem = fileName.trim().replace(/\.json$/i, "");
    try {
      const response = await importVersionAsSnapshot(editor.baseID, {
        sourceVersionID,
        fileName: stem ? `${stem}.json` : undefined,
        folderPath: folderPath || undefined,
      });

      if (!response.ok) {
        const detail = _.get(response, "error.error") || response.error;
        setFailure(String(detail));
        createSnackbar?.(`Import failed: ${detail}`, "error", false);
        return;
      }

      const created = _.get(response, "data.fileName");
      const landedIn = _.get(response, "data.folderPath");
      createSnackbar?.(
        landedIn ? `Imported as "${created}" in ${landedIn}.` : `Imported as "${created}".`,
        "success",
        false
      );
      await editor.refreshSnapshots();
      if (landedIn) onImported?.(landedIn);
      onClose();
    } finally {
      setIsImporting(false);
    }
  }

  return (
    <Dialog open={Boolean(open)} onClose={isImporting ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Import a version as a snapshot</DialogTitle>
      <DialogContent>
        {sourceCompanies.length > 1 && (
          <TextField
            className={classes.field}
            label="Source company"
            value={sourceCompanyID}
            onChange={(event) => setSourceCompanyID(event.target.value)}
            disabled={isImporting}
            select
            fullWidth
            helperText="Versions are held per company, so this decides both lists below."
          >
            {sourceCompanies.map((company) => (
              <MenuItem key={company._id} value={String(company._id)}>
                {company.name}
              </MenuItem>
            ))}
          </TextField>
        )}

        <TextField
          className={classes.field}
          label="Source base"
          value={sourceBaseID}
          onChange={(event) => setSourceBaseID(event.target.value)}
          disabled={isImporting || areBasesLoading}
          select
          fullWidth
          helperText={
            areBasesLoading
              ? "Loading…"
              : sourceBases.length === 0
                ? "No playEngine base you can reach in this company."
                : " "
          }
        >
          {baseOptions.map((option) =>
            option.isHeader ? (
              <ListSubheader key={option.key} disableSticky>
                {option.label}
              </ListSubheader>
            ) : (
              <MenuItem key={option.key} value={option.value}>
                {option.label}
              </MenuItem>
            )
          )}
        </TextField>

        <TextField
          className={classes.field}
          label="Version"
          value={sourceVersionID}
          onChange={(event) => setSourceVersionID(event.target.value)}
          disabled={isImporting || !sourceBaseID || areVersionsLoading}
          select
          fullWidth
          SelectProps={{
            onOpen: () => setVersionQuery(""),
            // Otherwise the Menu puts the focus on an option, away from the box.
            MenuProps: { autoFocus: false },
          }}
          helperText={
            areVersionsLoading
              ? "Loading…"
              : sourceBaseID && versions.length === 0
                ? "That base has no versions yet."
                : " "
          }
        >
          <VersionSearchRow
            query={versionQuery}
            onQueryChange={setVersionQuery}
            matchCount={versionMatchCount}
            total={versions.length}
          />
          {listedVersions.map((version) => (
            <MenuItem
              key={version.versionID}
              value={version.versionID}
              style={version.isHidden ? { display: "none" } : undefined}
            >
              {version.title || "(untitled)"}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          className={classes.field}
          label="Folder"
          value={folderPath}
          onChange={(event) => setFolderPath(event.target.value)}
          disabled={isImporting || folders.length === 0}
          select
          fullWidth
          // "" is a real choice here — the root — so the label has to stay up,
          // or it sits on top of the rendered "Root".
          InputLabelProps={{ shrink: true }}
          helperText={folders.length === 0 ? "No folders yet — it lands at the root." : " "}
        >
          <MenuItem value="">
            <em>Root</em>
          </MenuItem>
          {folders.map((folder) => (
            <MenuItem key={folder} value={folder}>
              {folder}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          className={classes.field}
          label="Snapshot name (optional)"
          value={fileName}
          onChange={(event) => setFileName(event.target.value)}
          disabled={isImporting}
          fullWidth
          placeholder="Taken from the version title"
        />

        <Typography className={classes.hint}>
          The version&apos;s images, fonts and sounds are copied into the snapshot, and its design into a new
          design file of its own, so nothing edited here changes the version it came from.
        </Typography>

        {failure && <Typography className={classes.failure}>{failure}</Typography>}
        {isImporting && (
          <div className={classes.busy}>
            <CircularProgress size={16} />
            <Typography className={classes.hint}>Copying assets…</Typography>
          </div>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isImporting}>
          Cancel
        </Button>
        <Button onClick={runImport} disabled={!canImport} color="primary" variant="contained">
          Import
        </Button>
      </DialogActions>
    </Dialog>
  );
}
